import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { listscheduleExam } from '../../actions/auth'; // Adjust path as needed
import {
    Grid, Typography, Box, Container, FormControl, InputLabel, Select, MenuItem, Paper,
    Pagination, Alert as MuiAlert // Renamed Alert
} from '@mui/material';
import { useTheme } from "@mui/material"; // Import useTheme
import ScheduleCard from './ScheduleCard'; // Adjust path as needed
import Header from '../../components/Header'; // Adjust path as needed
import LoadingOverlay from '../../components/LoadingOverlay'; // Adjust path as needed
import { tokens } from "../../theme"; // Adjust path as needed (optional if using theme directly)

// --- Main Component ---
const Schedulelist = () => {
    const dispatch = useDispatch();
    const theme = useTheme();
    // const colors = tokens(theme.palette.mode); // Use theme directly

    // --- Redux State ---
    // Use more specific selector if possible, fall back to empty array/object
    const scheduledExamsData = useSelector(state => state.scheduledExams?.scheduled || { results: [], count: 0 });
    const error = useSelector(state => state.scheduledExams?.error);
    // Memoize results and count separately
    const scheduledResults = useMemo(() => scheduledExamsData.results || [], [scheduledExamsData.results]);
    const totalCount = useMemo(() => scheduledExamsData.count || 0, [scheduledExamsData.count]);


    // --- Component State ---
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(12); // Default page size (e.g., 12 for 3/4 column layout)
    const [loading, setLoading] = useState(true);

    // --- Data Fetching ---
    const fetchSchedules = useCallback(() => {
        setLoading(true);
        dispatch(listscheduleExam(currentPage, pageSize)) // Pass pageSize to action
            .catch((fetchError) => {
                console.error("Error fetching schedules:", fetchError);
                // Error state is handled by the selector, just log here if needed
            })
            .finally(() => {
                setLoading(false);
            });
    }, [dispatch, currentPage, pageSize]);

    useEffect(() => {
        fetchSchedules();
    }, [fetchSchedules]); // Effect depends on the fetch function itself

    // --- Event Handlers ---
    const handlePageChange = useCallback((event, page) => {
        setCurrentPage(page);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, []);

    const handlePageSizeChange = useCallback((event) => {
        const newPageSize = parseInt(event.target.value, 10);
        setPageSize(newPageSize);
        setCurrentPage(1); // Reset to first page when page size changes
         // Fetching is handled by the useEffect dependency change
    }, []);

    // --- Data Processing (Sorting & Grouping) ---
    const groupedAndSortedExams = useMemo(() => {
        return scheduledResults
            // 1. Sort by start_time (ascending, earliest first)
            .sort((a, b) => new Date(b.start_time) - new Date(a.start_time))
            // 2. Group by Month and Year of start_time
            .reduce((acc, exam) => {
                const startDate = new Date(exam.start_time);
                // Use a consistent key format for sorting groups later (YYYY-MM)
                const groupKey = startDate.toLocaleString('en-CA', { year: 'numeric', month: '2-digit' }); // YYYY-MM format
                const displayMonthYear = startDate.toLocaleString('en-US', { month: 'long', year: 'numeric' });

                if (!acc[groupKey]) {
                    acc[groupKey] = { display: displayMonthYear, exams: [] };
                }
                acc[groupKey].exams.push(exam);
                return acc;
            }, {});
    }, [scheduledResults]);

     // Sort the group keys (YYYY-MM) to ensure chronological display of groups
    const sortedGroupKeys = useMemo(() => Object.keys(groupedAndSortedExams).sort(), [groupedAndSortedExams]);

    // --- "Today" Count Calculation ---
    const totalTodayExams = useMemo(() => {
        const todayStart = new Date();
        todayStart.setHours(0, 0, 0, 0);
        const todayEnd = new Date();
        todayEnd.setHours(23, 59, 59, 999);

        return scheduledResults.filter(exam => {
            const examStartTime = new Date(exam.start_time);
            return examStartTime >= todayStart && examStartTime <= todayEnd;
        }).length;
    }, [scheduledResults]);


    // --- Render Logic ---

    // Handle loading state
    if (loading && scheduledResults.length === 0) { // Show overlay only on initial load or when results are empty
        return <LoadingOverlay text="Loading Schedules..." />;
    }

    const totalPages = Math.ceil(totalCount / pageSize);

    return (
        <Container sx={{ py: theme.spacing(3) }}> {/* Use theme spacing */}
            <Header
                title="SCHEDULED EXAMS"
                subtitle={`Total Scheduled for Today: ${totalTodayExams}`}
                page='scheduleList'
            />

            {/* Top Controls: Page Size */}
             {/* <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: theme.spacing(3) }}>
                 <FormControl variant="outlined" size="small" sx={{ minWidth: 120 }}>
                     <InputLabel id="page-size-label">Items per page</InputLabel>
                     <Select
                         labelId="page-size-label"
                         label="Items per page"
                         value={pageSize}
                         onChange={handlePageSizeChange}
                     >
                         <MenuItem value={12}>12</MenuItem>
                         <MenuItem value={24}>24</MenuItem>
                         <MenuItem value={36}>36</MenuItem>
                         <MenuItem value={48}>48</MenuItem>
                     </Select>
                 </FormControl>
             </Box> */}

             {/* Handle Error State */}
             {error && (
                 <MuiAlert severity="error" sx={{ mb: theme.spacing(3) }}>
                     Error loading schedules: {typeof error === 'string' ? error : 'Please try again later.'}
                 </MuiAlert>
             )}

             {/* Main Content: Groups or Empty State */}
             {!loading && scheduledResults.length === 0 && !error ? (
                 <Paper elevation={2} sx={{ textAlign: 'center', p: theme.spacing(5), mt: theme.spacing(3), backgroundColor: theme.palette.background.default }}>
                    <Typography variant="h6" color="text.secondary">No scheduled exams found.</Typography>
                    {/* Optional: Add a button to schedule an exam? */}
                 </Paper>
             ) : (
                 sortedGroupKeys.map((groupKey) => {
                    const group = groupedAndSortedExams[groupKey];
                    return (
                        <Box key={groupKey} mb={theme.spacing(5)}> {/* Spacing between month groups */}
                             {/* Styled Month/Year Header */}
                             <Paper elevation={1} sx={{
                                 p: theme.spacing(1.5, 2), // Padding
                                 mb: theme.spacing(3), // Margin bottom before grid
                                 // backgroundColor: theme.palette.grey[100], // Subtle background
                                 borderLeft: `4px solid ${theme.palette.primary.main}`, // Accent border
                                 borderRadius: 1
                             }}>
                                 <Typography variant="h5" component="h2" sx={{ fontWeight: 'medium' }}>
                                     {group.display}
                                 </Typography>
                             </Paper>
                             {/* Grid for Cards */}
                             <Grid container spacing={3}>
                                 {group.exams.map(exam => (
                                     <ScheduleCard
                                         key={exam.id}
                                         exam={exam}
                                         // Pass refresh only if ScheduleCard actually uses it
                                         onDeleted={fetchSchedules}
                                     />
                                 ))}
                             </Grid>
                         </Box>
                     )
                 })
             )}

            {/* Pagination Controls */}
            {totalPages > 1 && (
                 <Box display="flex" justifyContent="center" alignItems="center" mt={theme.spacing(4)} p={theme.spacing(2)} component={Paper} elevation={1} sx={{borderRadius: 2}}>
                     <Pagination
                         count={totalPages}
                         page={currentPage}
                         onChange={handlePageChange}
                         color="primary"
                         showFirstButton
                         showLastButton
                         // Removed focus override for accessibility
                     />
                     {/* Optional: Display page info clearly */}
                     {/* <Typography variant="body2" sx={{ ml: 2 }}>
                         Page {currentPage} of {totalPages} ({totalCount} items)
                     </Typography> */}
                 </Box>
             )}
        </Container>
    );
};

export default Schedulelist;